import { useEffect } from 'react';
import type { NovelTheme } from '../components/ReaderSettings';
import { setStorageItem } from '../utils/storage';
import { isDarkTheme } from '../utils/theme';

const themeMetaColors: Record<Exclude<NovelTheme, 'auto'>, string> = {
  paper: '#FAF8F5',
  sepia: '#F5ECDB',
  sakura: '#FAF0F2',
  night: '#18181A',
  midnight: '#0B0F17',
};

export function useAppThemeEffect(currentTheme: NovelTheme) {
  useEffect(() => {
    setStorageItem('novelflow_theme', currentTheme);

    const updateThemeSettings = () => {
      let activeTheme: Exclude<NovelTheme, 'auto'>;
      if (currentTheme === 'auto') {
        const systemIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        activeTheme = systemIsDark ? 'midnight' : 'paper';
      } else {
        activeTheme = currentTheme;
      }

      // Tailwind の dark: バリアントを動作させるため、documentElementにdarkクラスを反映
      const isDark = isDarkTheme(activeTheme);
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      // iOS/PWAのステータスバーとテーマカラーを動的に更新
      const color = themeMetaColors[activeTheme] || '#FAF8F5';

      // theme-colorの更新
      let themeMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeMeta) {
        themeMeta = document.createElement('meta');
        themeMeta.setAttribute('name', 'theme-color');
        document.head.appendChild(themeMeta);
      }
      themeMeta.setAttribute('content', color);
    };

    updateThemeSettings();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (currentTheme === 'auto') {
        updateThemeSettings();
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [currentTheme]);
}
